/**
 * Cliente REST do musa-agreements para a coleta sob demanda.
 *
 * Mesmo desenho da contestação: chamada direta ao serviço com o token do próprio usuário
 * (realm `generator`, ou `musa` para o time interno). O serviço deriva do token os geradores que
 * a pessoa pode ver e operar — o `generatorId` enviado aqui só restringe dentro desse escopo.
 */

import { getValidToken } from '@/integrations/keycloak/client';

const API_URL = import.meta.env.VITE_AGREEMENTS_API_URL;

/** Espelha `CollectRequestStatusEnum` do serviço. */
export enum OnDemandRequestStatus {
  PENDING = 1,
  ACCEPTED = 2,
  REJECTED = 3,
  CANCELED = 4,
}

/** 1 = diurno, 2 = noturno. Nulo = sem preferência. */
export enum OnDemandPeriod {
  DIURNO = 1,
  NOTURNO = 2,
}

export type OnDemandAdvanceUnit = 'HOURS' | 'DAYS';

/** Regra copiada para o contrato na criação dele. */
export interface OnDemandContractRule {
  ruleId: number | null;
  ruleName: string | null;
  minAdvanceValue: number;
  minAdvanceUnit: OnDemandAdvanceUnit;
  monthlyLimit: number | null;
}

/** `NOT_CONTRACTED` = limite 0 (serviço não contratado); `MONTHLY_LIMIT_REACHED` = o mês acabou. */
export type OnDemandContractBlockReason = 'BUCKET_PACKAGING' | 'NOT_CONTRACTED' | 'MONTHLY_LIMIT_REACHED';

export interface OnDemandAddress {
  id: number;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
}

export interface OnDemandGenerator {
  id: number;
  name: string;
  fantasyName: string | null;
  legalName: string | null;
}

export interface OnDemandHauler {
  id: number;
  fantasyName: string | null;
  legalName: string | null;
}

export interface OnDemandNamed {
  id: number;
  name: string | null;
}

export interface OnDemandContract {
  contractId: number;
  /** `YYYY-MM-DD`. */
  startsAt: string | null;
  /** `YYYY-MM-DD`; nulo = sem fim. */
  endsAt: string | null;
  generator: OnDemandGenerator;
  address: OnDemandAddress;
  hauler: OnDemandHauler;
  material: OnDemandNamed;
  packaging: OnDemandNamed;
  /** Nulo em contrato anterior às regras: sem limite nem antecedência. */
  rule: OnDemandContractRule | null;
  /** `YYYY-MM`. */
  month: string;
  used: number;
  /** Nulo = sem limite. */
  remaining: number | null;
  requestable: boolean;
  blockReason: OnDemandContractBlockReason | null;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface OnDemandPerson {
  name: string;
  phone: string;
  email?: string | null;
}

export interface OnDemandTimeRestriction {
  /** `HH:mm`. */
  start: string;
  /** `HH:mm`. */
  end: string;
}

export interface CreateOnDemandRequestsInput {
  dates: Array<{
    /** `YYYY-MM-DD`. */
    date: string;
    period?: OnDemandPeriod | null;
    timeRestrictions?: OnDemandTimeRestriction[];
  }>;
  quantity: number;
  photoUrls?: string[];
  observations?: string | null;
  requester: OnDemandPerson;
  /** Ausente = o próprio solicitante acompanha. */
  companion?: OnDemandPerson | null;
}

export interface OnDemandRequest {
  id: string;
  contractId: number;
  batchId: string;
  /** `YYYY-MM-DD`. */
  plannedDate: string;
  period: OnDemandPeriod | null;
  status: OnDemandRequestStatus;
  quantity: number;
  photoUrls: string[];
  observations: string | null;
  requester: OnDemandPerson;
  companion: OnDemandPerson;
  collectId: string | null;
  decidedAt: string | null;
  canceledAt: string | null;
  createdBy: string;
  createdAt: string | null;
}

export interface OnDemandRequestListItem extends OnDemandRequest {
  generator: OnDemandGenerator;
  address: OnDemandAddress;
  hauler: OnDemandHauler;
  material: OnDemandNamed;
  packaging: OnDemandNamed;
}

export class AgreementsApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AgreementsApiError';
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Recusa de regra (antecedência, limite, vigência, caçamba): a mensagem é para o usuário. */
  get isBusinessRule(): boolean {
    return this.status === 400 || this.status === 409;
  }
}

/**
 * Corpo de erro. O `GlobalInterceptor` do serviço devolve `{ ok: false, error, errors }`, com
 * as mensagens de validação e de regra em `errors`. `message` é o formato padrão do NestJS,
 * mantido para resposta que não passe pelo interceptor.
 */
interface ApiErrorBody {
  ok?: false;
  error?: string;
  errors?: string[];
  message?: string | string[];
}

export function messageFromBody(body: ApiErrorBody | null, status: number): string {
  if (body?.errors?.length) return body.errors.join(' ');

  const message = Array.isArray(body?.message) ? body?.message.join(' ') : body?.message;
  return message || body?.error || `Erro ${status} no serviço de coletas`;
}

async function request<T>(
  path: string,
  init?: { method?: string; body?: unknown; formData?: FormData; signal?: AbortSignal },
): Promise<T> {
  const token = await getValidToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };

  // multipart: o navegador monta o Content-Type com o boundary
  if (!init?.formData) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${API_URL}${path}`, {
    method: init?.method ?? 'GET',
    headers,
    body: init?.formData ?? (init?.body != null ? JSON.stringify(init.body) : undefined),
    signal: init?.signal,
  });

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      body = null;
    }
    throw new AgreementsApiError(res.status, messageFromBody(body, res.status));
  }

  if (res.status === 204) return undefined as T;

  // o GlobalInterceptor do serviço embrulha toda resposta de sucesso em `{ data }`
  const body = (await res.json()) as { data: T };
  return body.data;
}

/**
 * Baixa um arquivo (ZIP) com teto de tempo. Estourado o teto, o `fetch` rejeita com `AbortError`.
 * Erro HTTP vem no envelope do GlobalInterceptor e vira `AgreementsApiError`.
 */
async function requestBlob(path: string, body: unknown, timeoutMs: number): Promise<Blob> {
  const token = await getValidToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) {
      let errorBody: ApiErrorBody | null = null;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        errorBody = null;
      }
      throw new AgreementsApiError(res.status, messageFromBody(errorBody, res.status));
    }
    return await res.blob();
  } finally {
    clearTimeout(timeout);
  }
}

type QueryValue = string | number | Array<string | number> | undefined | null;

/** Lista vai separada por vírgula (`status=1,2`), o formato que o serviço normaliza. */
export function queryString(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      if (value.length) search.set(key, value.join(','));
    } else if (value != null && value !== '') {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export interface ListOnDemandContractsFilter {
  generatorId?: number;
  /** `YYYY-MM`. Ausente = mês corrente. */
  month?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchOnDemandContracts(
  filter: ListOnDemandContractsFilter = {},
): Promise<Paginated<OnDemandContract>> {
  return request(`/ops/generator/on-demand/contracts${queryString({ ...filter })}`);
}

export async function createOnDemandRequests(
  contractId: number,
  input: CreateOnDemandRequestsInput,
): Promise<OnDemandRequest[]> {
  return request(`/ops/generator/on-demand/contracts/${contractId}/requests`, {
    method: 'POST',
    body: input,
  });
}

export interface ListOnDemandRequestsFilter {
  /** Um status só: o serviço não aceita lista aqui. */
  status?: OnDemandRequestStatus;
  /** Um gerador exato do escopo do token — sem os filhos. */
  generatorId?: number;
  /** `YYYY-MM-DD`. */
  plannedDateFrom?: string;
  /** `YYYY-MM-DD`. */
  plannedDateTo?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchOnDemandRequests(
  filter: ListOnDemandRequestsFilter = {},
): Promise<Paginated<OnDemandRequestListItem>> {
  return request(`/ops/generator/on-demand/requests${queryString({ ...filter })}`);
}

/** Tipos que o serviço aceita como foto da solicitação. */
export const PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png'] as const;

export const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024;

/** Sobe o arquivo e devolve a URL pública que vai em `photoUrls`. */
export async function uploadOnDemandPhoto(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const { url } = await request<{ url: string }>('/ops/documents/upload', {
    method: 'POST',
    formData,
  });

  return url;
}

/**
 * O bucket das fotos é privado: a URL que o upload devolve só abre assinada. Gera um link
 * temporário (o serviço assina por alguns minutos) para mostrar a foto.
 */
export async function fetchPresignedPhotoUrl(url: string): Promise<string> {
  const { url: signed } = await request<{ url: string }>('/ops/documents/presigned-url', {
    method: 'POST',
    body: { url },
  });

  return signed;
}

/**
 * Reassina a URL para download: o serviço assina com `Content-Disposition: attachment` e o
 * navegador salva o arquivo com `filename` em vez de abri-lo. Necessário porque os buckets não
 * têm CORS — `<a download>` em outra origem é ignorado e `fetch` + blob é bloqueado.
 */
export async function fetchPresignedDownloadUrl(url: string, filename: string): Promise<string> {
  const { url: signed } = await request<{ url: string }>('/ops/documents/presigned-url', {
    method: 'POST',
    body: { url, download_filename: filename },
  });

  return signed;
}

// ─── Caçamba ─────────────────────────────────────────────────────────────────────────────────
// O pedido do gerador agenda uma movimentação de equipamento que nasce pendente do transportador —
// o mesmo fluxo que o Hub usa (colocação, retirada, troca). O transportador aceita ou recusa no
// portal dele.

/** Espelha `BucketMovementTypeEnum`. */
export enum BucketMovementType {
  PLACEMENT = 1,
  REMOVAL = 2,
  SWAP = 3,
}

/** Espelha `BucketMovementStatusEnum`. */
export enum BucketMovementStatus {
  PENDING_HAULER = 1,
  PLANNED = 2,
  DONE = 3,
  BLOCKED = 4,
  CANCELLED = 5,
}

/** Espelha `BucketLocationTypeEnum`. */
export enum BucketLocationType {
  PUBLIC_ROAD = 1,
  PRIVATE_AREA = 2,
}

export interface BucketContractRule {
  ruleId: number | null;
  ruleName: string | null;
  minAdvanceValue: number;
  minAdvanceUnit: OnDemandAdvanceUnit;
  /** Pedido para hoje até `sameDayCutoff` (`HH:MM` de Brasília); depois dele, vale a antecedência. */
  sameDayAllowed: boolean;
  sameDayCutoff: string | null;
}

export interface BucketContract {
  contractId: number;
  /** `YYYY-MM-DD`. */
  startsAt: string | null;
  endsAt: string | null;
  generator: OnDemandGenerator;
  address: OnDemandAddress;
  hauler: OnDemandHauler;
  material: OnDemandNamed;
  packaging: OnDemandNamed;
  /**
   * Regra de solicitação que vale para o pedido, resolvida pelo serviço na hora (contrato de
   * caçamba não guarda regra). Só a antecedência se aplica à caçamba.
   */
  rule: BucketContractRule;
  maxDaysPublic: number;
  maxDaysPrivate: number;
  /** Caçambas na rua agora. */
  openCount: number;
  /** Colocações e trocas ainda por acontecer. */
  plannedCount: number;
  /** Nulo = sem limite de simultâneas. */
  simultaneousLimit: number | null;
  totalContracted: number;
  totalUsedCount: number;
  /** Na rua + planejadas passa do limite. Só avisa: o pedido é aceito. */
  aboveLimit: boolean;
  /**
   * O contrato controla o número de cada caçamba: a retirada pedida pelo gerador informa qual
   * sai. Ausente (serviço anterior ao campo) vale como falso.
   */
  requiresNumber?: boolean;
}

export interface BucketStay {
  id: string;
  bucketNumber: string | null;
  locationType: BucketLocationType;
  placedAt: string | null;
  /** `YYYY-MM-DD`. */
  dueAt: string | null;
  /** Já há retirada ou troca agendada para ela (`YYYY-MM-DD`). */
  scheduledFor: string | null;
}

export interface CreateBucketMovementInput {
  type: BucketMovementType;
  /** `YYYY-MM-DD`. */
  plannedDate: string;
  /** Obrigatório fora da retirada. */
  locationType?: BucketLocationType | null;
  /** Quem pediu e como falar com essa pessoa. Visível ao transportador. */
  requesterNote?: string | null;
  /** Turno pedido; nulo = sem preferência. */
  period?: OnDemandPeriod | null;
  /** A caçamba na rua a retirar ou trocar. Opcional: sem ela, o transportador informa no registro. */
  stayId?: string | null;
  /** Número da caçamba a retirar. Só na retirada, e exigido quando o contrato controla número. */
  bucketNumber?: string | null;
  /** Fotos de evidência do pedido: as URLs devolvidas por `uploadOnDemandPhoto`. */
  photoUrls?: string[];
}

export interface BucketWarning {
  code: string;
  message: string;
}

export interface BucketMovementResult {
  id: string;
  status: BucketMovementStatus;
  plannedDate: string;
  removal?: { id: string; plannedDate: string };
  /** Avisos de limite e de data — nunca bloqueiam. */
  warnings: BucketWarning[];
}

export interface BucketMovement {
  id: string;
  contractId: number;
  type: BucketMovementType;
  status: BucketMovementStatus;
  /** `YYYY-MM-DD`. */
  plannedDate: string | null;
  requesterNote: string | null;
  /** Turno pedido; nulo = sem preferência. */
  period: OnDemandPeriod | null;
  locationType: BucketLocationType | null;
  parentMovementId: string | null;
  /** A solicitação de onde veio: o mesmo em todas as operações pedidas num envio só. */
  requestId?: string | null;
  /** Números do que aconteceu: vazio enquanto a operação não foi registrada. */
  bucketNumbers: string[];
  /** Número que o gerador informou ao pedir a retirada. Ausente em serviço anterior ao campo. */
  requestedBucketNumber?: string | null;
  /** Fotos de evidência do pedido. O serviço sempre manda a lista; `fetchBucketMovements` garante `[]`. */
  photoUrls?: string[];
  executedAt: string | null;
  generator: OnDemandGenerator;
  address: OnDemandAddress;
  hauler: OnDemandHauler;
  material: OnDemandNamed;
  packaging: OnDemandNamed;
}

export async function fetchBucketContracts(
  filter: { generatorId?: number; page?: number; pageSize?: number } = {},
): Promise<Paginated<BucketContract>> {
  return request(`/ops/generator/buckets/contracts${queryString({ ...filter })}`);
}

export async function fetchBucketStays(contractId: number): Promise<BucketStay[]> {
  return request(`/ops/generator/buckets/contracts/${contractId}/stays`);
}

/** Resultado de um item do lote. */
export type BucketBatchItem =
  | ({ index: number; ok: true } & BucketMovementResult)
  | { index: number; ok: false; error: string };

/**
 * Resposta do lote: um resultado por item, na ordem enviada. Parte pode entrar e parte não —
 * o serviço responde 207 nesse caso, que continua sendo sucesso HTTP.
 */
export interface BucketBatchResult {
  succeeded: number;
  failed: number;
  results: BucketBatchItem[];
}

/** Várias operações no mesmo contrato numa chamada (até 50). */
export async function createBucketMovements(
  contractId: number,
  inputs: CreateBucketMovementInput[],
): Promise<BucketBatchResult> {
  return request(`/ops/generator/buckets/contracts/${contractId}/movements`, {
    method: 'POST',
    body: inputs,
  });
}

export interface ListBucketMovementsFilter {
  /** Vários status são um OU. */
  status?: BucketMovementStatus[];
  /** Vários tipos são um OU. */
  type?: BucketMovementType[];
  /** Um gerador exato do escopo do token — sem os filhos. */
  generatorId?: number;
  /** Resíduo do contrato. */
  materialId?: number;
  /** Acondicionamento do contrato. */
  packagingId?: number;
  /** `YYYY-MM-DD`, inclusive. */
  plannedFrom?: string;
  /** `YYYY-MM-DD`, inclusive. */
  plannedTo?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchBucketMovements(
  filter: ListBucketMovementsFilter = {},
): Promise<BucketMovement[]> {
  const movements = await request<BucketMovement[]>(`/ops/generator/buckets/movements${queryString({ ...filter })}`);
  // movimentação de antes das fotos (ou serviço ainda sem o campo) chega sem a lista
  return movements.map((movement) => ({ ...movement, photoUrls: movement.photoUrls ?? [] }));
}

/**
 * Uma solicitação de caçamba: o que o gerador pediu num envio só ("4 retiradas"), com as
 * operações que ela gerou. `id` é o da solicitação, ou o da operação quando ela veio sozinha.
 */
export interface BucketRequest {
  id: string;
  movements: BucketMovement[];
}

/** As solicitações do gerador, uma por envio. Pagina solicitações, não operações. */
export async function fetchBucketRequests(filter: ListBucketMovementsFilter = {}): Promise<BucketRequest[]> {
  const requests = await request<BucketRequest[]>(`/ops/generator/buckets/requests${queryString({ ...filter })}`);

  return requests.map((item) => ({
    ...item,
    movements: item.movements.map((movement) => ({ ...movement, photoUrls: movement.photoUrls ?? [] })),
  }));
}

// ========== Registry (catálogo de resíduos e acondicionamentos) ==========

/** Item de `GET /api/v1/registry/material` — só o que o portal consome. */
export interface RegistryMaterial {
  id: number;
  name: string;
  /** Código IBAMA do resíduo; nulo quando o cadastro não tem. */
  ibama_code: string | null;
  /** Unidade de medida do realizado (`kg`, `lt`, …). */
  unity?: string | null;
}

/** Item de `GET /api/v1/registry/packaging`. `type` distingue caçamba (`open_bucket`/`closed_bucket`) das demais. */
export interface RegistryPackaging {
  id: number;
  name: string;
  type: string;
}

interface RegistryMaterialItem {
  id: number;
  name: string;
  ibamaCode?: string | null;
  unity?: string | null;
}

/** O catálogo tem poucas dezenas de itens; uma página só evita paginar no front. */
const REGISTRY_PAGE_SIZE = 1000;

export async function fetchRegistryMaterials(): Promise<RegistryMaterial[]> {
  const items = await request<RegistryMaterialItem[] | null>(
    `/api/v1/registry/material${queryString({ pageSize: REGISTRY_PAGE_SIZE })}`,
  );
  return (items ?? []).map((m) => ({ id: m.id, name: m.name, ibama_code: m.ibamaCode ?? null, unity: m.unity ?? null }));
}

export async function fetchRegistryPackagings(): Promise<RegistryPackaging[]> {
  const items = await request<RegistryPackaging[] | null>(
    `/api/v1/registry/packaging${queryString({ pageSize: REGISTRY_PAGE_SIZE })}`,
  );
  return (items ?? []).map((p) => ({ id: p.id, name: p.name, type: p.type }));
}

// ========== Coletas do Portal do Gerador (/ops/generator/collects) ==========

/**
 * Listas repetem a chave (`status=planned&status=fault`), formato que o serviço aceita ao lado de
 * `a=1,2`. Nulo, texto vazio e lista vazia ficam de fora.
 */
export function repeatedQueryString(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(key, String(item)));
    } else if (value != null && value !== '') {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Filtro das listagens de coletas, no vocabulário que os hooks sempre usaram (snake_case, herdado
 * do gateway). O client traduz para a query da rota.
 */
export interface CollectsFilterInput {
  page: number;
  page_size: number;
  /** Janela de `planned_date`. Obrigatória sem `collect_id`; opcional com ele (cada ponta que vier ainda recorta). */
  date_from?: string;
  date_to?: string;
  /**
   * Coletas específicas (uuid, até 500 por chamada), cruzadas com o escopo e os demais filtros.
   * Uso: coletas de uma fatura de valor variável vindas do faturamento-api. Id fora do escopo
   * simplesmente não volta.
   */
  collect_id?: string[] | null;
  /** Geradores (qualquer nível da árvore); o serviço cruza com o escopo do token. */
  generator_id?: number[] | null;
  material_id?: number[] | null;
  /** Siglas das UFs do endereço da coleta. */
  uf?: string[] | null;
  /** Filiais (gerador da coleta). */
  establishment_id?: number[] | null;
  /** Status da coleta (ex.: ['planned'] em Próximas Coletas). Ausente = todos. */
  status?: string[] | null;
  /** Exclui status específicos (ex.: ['planned'] no Histórico). */
  exclude_status?: string[] | null;
  /** Direção por planned_date. Default no serviço: 'desc'. */
  order_direction?: 'asc' | 'desc' | null;
  /** Esconde as coletas planejadas desses materiais (ex.: RCC e Óleo). */
  exclude_planned_material_ids?: number[] | null;
  /**
   * Aceito pelos hooks por compatibilidade com o filtro do download em massa, mas **nunca enviado**
   * às rotas de coleta: o agreements responde 400 enquanto não enxerga o mtr-manager (Fase 2).
   */
  has_mtr?: boolean | null;
}

export interface CollectsMaterial {
  id: number;
  name: string;
  ibama_code?: string | null;
  description?: string | null;
}

export interface CollectsPackaging {
  id: number;
  name: string;
}

export interface CollectsEstablishment {
  id: number;
  /** Raiz do `tree_id`; nulo em cadastro sem árvore. */
  generator_id: number | null;
  name: string;
  document: string | null;
  city?: string | null;
  state?: string | null;
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  zipcode?: string | null;
}

export interface CollectsItem {
  id: string;
  planned_date: string;
  /** Realizado na unidade do material (kg, ou litros se `unity='lt'`); 0 na planejada. */
  weight: number | null;
  /** Quantidade de acondicionamentos. */
  units: number | null;
  /** `weight × 1,1`. */
  volume: number | null;
  status?: string | null;
  has_collect_evidence?: boolean | null;
  /**
   * `null` = desconhecido: o agreements ainda não enxerga o mtr-manager (Fase 2). Nunca é `false`
   * por falta de dado — a tela não deve afirmar "sem MTR" a partir disto.
   */
  has_mtr?: boolean | null;
  has_cdf?: boolean | null;
  stop_point_id?: string | null;
  material: CollectsMaterial;
  packaging?: CollectsPackaging | null;
  establishment: CollectsEstablishment;
}

export interface CollectsResponse {
  data: CollectsItem[];
  pagination?: {
    page: number;
    last_page: number;
    page_size: number;
    count: number;
  } | null;
}

/** Recorte dos cards e do calendário: escopo sem paginação. */
export interface CollectSummaryFilterInput {
  generator_id?: number[] | null;
  establishment_id?: number[] | null;
  material_id?: number[] | null;
  uf?: string[] | null;
  date_from?: string | null;
  date_to?: string | null;
  exclude_planned_material_ids?: number[] | null;
}

export interface CollectSummaryItem {
  id: string;
  planned_date: string;
  status: string;
  establishment_name?: string | null;
  city?: string | null;
  state?: string | null;
  material_name?: string | null;
  packaging_name?: string | null;
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  zipcode?: string | null;
}

/** Um dia do calendário: até 5 coletas de amostra + total do dia. */
export interface CollectCalendarDay {
  date: string;
  total: number;
  collects: CollectSummaryItem[];
}

export interface CollectEvidence {
  evidence_before: string | null;
  evidence_after: string | null;
}

type CollectsScopeFilter = Pick<
  CollectsFilterInput,
  'generator_id' | 'establishment_id' | 'material_id' | 'uf' | 'exclude_planned_material_ids'
>;

/** Recorte comum às rotas do gerador, já nos nomes da query. */
function collectsScopeQuery(filter: CollectsScopeFilter): Record<string, QueryValue> {
  return {
    generatorId: filter.generator_id ?? undefined,
    establishmentId: filter.establishment_id ?? undefined,
    materialId: filter.material_id ?? undefined,
    uf: filter.uf ?? undefined,
    excludePlannedMaterialIds: filter.exclude_planned_material_ids ?? undefined,
  };
}

/** Lista paginada de coletas por janela de `planned_date`. Escopo pelo token; 403 se o gerador pedido está todo fora dele. */
export async function fetchCollects(filter: CollectsFilterInput): Promise<CollectsResponse> {
  const query = repeatedQueryString({
    ...collectsScopeQuery(filter),
    dateFrom: filter.date_from,
    dateTo: filter.date_to,
    collectId: filter.collect_id ?? undefined,
    status: filter.status ?? undefined,
    excludeStatus: filter.exclude_status ?? undefined,
    orderDirection: filter.order_direction ?? undefined,
    page: filter.page,
    pageSize: filter.page_size,
  });
  const result = await request<CollectsResponse | null>(`/ops/generator/collects${query}`);
  return result ?? { data: [], pagination: null };
}

/** Calendário Semana/Mês: só dias com coleta. Sem `status`, o serviço devolve só as planejadas. */
export async function fetchCollectsCalendar(filter: CollectsFilterInput): Promise<CollectCalendarDay[]> {
  const query = repeatedQueryString({
    ...collectsScopeQuery(filter),
    dateFrom: filter.date_from,
    dateTo: filter.date_to,
    status: filter.status ?? undefined,
  });
  const result = await request<CollectCalendarDay[] | null>(`/ops/generator/collects/calendar${query}`);
  return result ?? [];
}

/** Próxima coleta planejada a partir de `date_from` (hoje, se ausente). `null` quando não há. */
export async function fetchNextScheduledCollect(
  filter: CollectSummaryFilterInput,
): Promise<CollectSummaryItem | null> {
  const query = repeatedQueryString({
    ...collectsScopeQuery(filter),
    dateFrom: filter.date_from ?? undefined,
  });
  const result = await request<CollectSummaryItem | null>(`/ops/generator/collects/next${query}`);
  return result ?? null;
}

/**
 * Evidências de antes/depois com URL assinada; planejada devolve os dois nulos. Coleta que o
 * serviço não conhece (404) vira `null`, como o gateway respondia.
 */
export async function fetchCollectEvidence(collectId: string): Promise<CollectEvidence | null> {
  try {
    return await request<CollectEvidence>(`/ops/generator/collects/${encodeURIComponent(collectId)}/evidence`);
  } catch (error) {
    if (error instanceof AgreementsApiError && error.status === 404) return null;
    throw error;
  }
}

// ========== Dashboard do Portal do Gerador (/ops/generator/analytics) ==========

/** Filtro das seções com período da tela, no vocabulário dos hooks (snake_case). */
export interface DashboardFilterInput {
  date_from: string;
  date_to: string;
  /** Geradores (qualquer nível da árvore); o serviço cruza com o escopo do token. */
  generator_id: number[] | null;
  material_id: number[] | null;
  /** Siglas das UFs do endereço da coleta. */
  uf: string[] | null;
  /** Filiais (gerador da coleta). */
  establishment_id?: number[] | null;
}

/** Escopo sem datas — as seções de janela fixa ignoram o date picker. */
export type DashboardScopeFilterInput = Omit<DashboardFilterInput, 'date_from' | 'date_to'>;

export interface DashboardTotals {
  /** `null` enquanto o agreements não enxerga o mtr-manager (Fase 2): esconder o KPI, não mostrar 0. */
  mtr_rate: number | null;
  success_rate: number;
  total_collects: number;
  /** Total de coletas no período anterior de mesma duração. */
  total_collects_previous: number;
  /** Variação % de total_collects vs período anterior. `null` quando o período anterior teve zero coletas. */
  variation_percent: number | null;
  total_units: number;
  total_volume: number;
  total_weight: number;
  /** Unidades com pelo menos uma coleta no período. */
  total_establishments_with_collects: number;
}

export interface DashboardByMaterialPerDay {
  material: string;
  material_id: number;
  /** `YYYY-MM-DD` do dia. */
  date: string;
  /** `YYYY-MM-DD` da segunda-feira que abre a semana. */
  week: string;
  /** `YYYY-MM`. */
  month: string;
  total_collects: number;
  total_units: number;
  total_volume: number;
  total_weight: number;
}

export interface EstablishmentVolumePerDay {
  establishment_id: number;
  establishment_name: string;
  date: string;
  week: string;
  month: string;
  total_volume: number;
  /** Volume da unidade no intervalo inteiro — ordena as linhas. */
  establishment_total_volume: number;
}

export interface MonthlyCollectsByOrigin {
  /** Primeiro dia do mês, `YYYY-MM-DD`. */
  month: string;
  /** Coletas realizadas geradas pela grade do contrato. */
  recurring_count: number;
  /** Coletas realizadas criadas fora da grade. */
  on_demand_count: number;
}

export interface FaultReasonBreakdown {
  reason_id: number | null;
  reason_label: string;
  count: number;
  percentage: number;
}

function analyticsPeriodQuery(filter: DashboardFilterInput, extra: Record<string, QueryValue> = {}): string {
  return repeatedQueryString({
    ...collectsScopeQuery(filter),
    dateFrom: filter.date_from,
    dateTo: filter.date_to,
    ...extra,
  });
}

/** KPIs do Overview. */
export async function fetchDashboardTotals(filter: DashboardFilterInput, signal?: AbortSignal): Promise<DashboardTotals> {
  return request(`/ops/generator/analytics/totals${analyticsPeriodQuery(filter)}`, { signal });
}

/** Série "Coletas ao longo do tempo", por material e dia (só realizadas). */
export async function fetchDashboardByMaterialPerDay(
  filter: DashboardFilterInput,
  signal?: AbortSignal,
): Promise<DashboardByMaterialPerDay[]> {
  const result = await request<DashboardByMaterialPerDay[] | null>(
    `/ops/generator/analytics/by-material-per-day${analyticsPeriodQuery(filter)}`,
    { signal },
  );
  return result ?? [];
}

/** Volume diário das `limit` unidades de maior volume — mapa de calor "Volume coletado". */
export async function fetchDashboardVolumeByEstablishment(
  filter: DashboardFilterInput,
  limit = 6,
  signal?: AbortSignal,
): Promise<EstablishmentVolumePerDay[]> {
  const result = await request<EstablishmentVolumePerDay[] | null>(
    `/ops/generator/analytics/volume-by-establishment${analyticsPeriodQuery(filter, { limit })}`,
    { signal },
  );
  return result ?? [];
}

/**
 * Coletas recorrentes x sob demanda nos 4 meses mais recentes — janela fixa, sem datas (o serviço
 * ignora qualquer data). Sempre 4 itens, inclusive os zerados: não filtrar por `count > 0`.
 */
export async function fetchDashboardCollectsByOrigin(
  filter: DashboardScopeFilterInput,
  signal?: AbortSignal,
): Promise<MonthlyCollectsByOrigin[]> {
  const result = await request<MonthlyCollectsByOrigin[] | null>(
    `/ops/generator/analytics/collects-by-origin${repeatedQueryString(collectsScopeQuery(filter))}`,
    { signal },
  );
  return result ?? [];
}

/** Ranking dos motivos de falha mais frequentes do período — widget "Taxa de sucesso". */
export async function fetchDashboardFaultReasons(
  filter: DashboardFilterInput,
  limit = 4,
  signal?: AbortSignal,
): Promise<FaultReasonBreakdown[]> {
  const result = await request<FaultReasonBreakdown[] | null>(
    `/ops/generator/analytics/fault-reasons${analyticsPeriodQuery(filter, { limit })}`,
    { signal },
  );
  return result ?? [];
}

// ========== Dashboard — carbono evitado ==========

export interface MaterialCarbonBreakdown {
  /** `null` na entrada agregada "Outros". */
  material_id: number | null;
  material_name: string;
  /** Sempre em **kg** de CO₂ — a conversão para tonelada é de exibição. */
  carbon_saved: number;
  is_aggregated: boolean;
}

/**
 * Carbono evitado por resíduo — alimenta o treemap do Overview. Os `limit` maiores mais "Outros"
 * quando sobra algo; UF vira restrição sobre geradores com endereço na UF.
 */
export async function fetchDashboardCarbonByMaterial(
  filter: DashboardFilterInput,
  limit = 8,
  signal?: AbortSignal,
): Promise<MaterialCarbonBreakdown[]> {
  const result = await request<MaterialCarbonBreakdown[] | null>(
    `/ops/generator/analytics/carbon-by-material${analyticsPeriodQuery(filter, { limit })}`,
    { signal },
  );
  return result ?? [];
}

// ========== Indicadores ambientais — CO₂ (/ops/generator/environmental) ==========

export interface EnvironmentalDataFilter {
  /** `YYYY-MM`. */
  month?: string | null;
  /** `YYYY-MM`; prevalece sobre `month`. Meses futuros são descartados pelo serviço. */
  months?: string[] | null;
  generator_id?: number[] | null;
  establishment_id?: number[] | null;
  material_id?: number[] | null;
}

export interface EnvironmentalMaterialInfo {
  id: number;
  name: string;
}

export interface EnvironmentalMaterialTotal {
  /** Metais | Plástico | Papel | Vidro | Reciclável | Outros. */
  group: string;
  material: EnvironmentalMaterialInfo;
  /** kg de material. */
  weight_collected: number;
  /** kg de CO₂. */
  carbon_saved: number;
}

export interface EnvironmentalCo2Totals {
  carbon_saved: number;
  /** Os N meses imediatamente antes da seleção. */
  carbon_saved_previous: number;
  /** `null` sem base de comparação. */
  variation_percent: number | null;
  weight_collected: number;
}

export interface EnvironmentalCo2ByMonth {
  /** `YYYY-MM`; 12 meses terminando no mais recente selecionado, zerados onde não há dado. */
  month: string;
  carbon_saved: number;
}

/** Linha do ranking como o serviço devolve: a unidade vem só com o id (o nome mora no /generators/me). */
export interface EnvironmentalUnitRankingRow {
  carbon_saved: number;
  group: string;
  establishment: { id: number };
  material: EnvironmentalMaterialInfo;
}

export interface EnvironmentalDataResponse {
  co2_totals: EnvironmentalCo2Totals | null;
  material_totals: EnvironmentalMaterialTotal[];
  co2_evolution: EnvironmentalCo2ByMonth[];
  unit_ranking: EnvironmentalUnitRankingRow[];
}

const EMPTY_ENVIRONMENTAL: EnvironmentalDataResponse = {
  co2_totals: null,
  material_totals: [],
  co2_evolution: [],
  unit_ranking: [],
};

/** Totais, por material, evolução e ranking de unidades. Sem mês: a última competência com dado no escopo. */
export async function fetchEnvironmentalData(filter: EnvironmentalDataFilter): Promise<EnvironmentalDataResponse> {
  const query = repeatedQueryString({
    month: filter.month ?? undefined,
    months: filter.months ?? undefined,
    generatorId: filter.generator_id ?? undefined,
    establishmentId: filter.establishment_id ?? undefined,
    materialId: filter.material_id ?? undefined,
  });
  const result = await request<EnvironmentalDataResponse | null>(`/ops/generator/environmental${query}`);
  return result ?? EMPTY_ENVIRONMENTAL;
}

/** Última competência (`YYYY-MM`) com dado de CO₂ no escopo; `null` sem dado. */
export async function fetchEnvironmentalLastAvailableMonth(): Promise<string | null> {
  const result = await request<{ month: string | null } | null>('/ops/generator/environmental/last-available-month');
  return result?.month ?? null;
}

// ========== Certificados Planton (/ops/generator/planton) ==========

/** processando | disponivel | erro | sem_coletas. */
export type PlantonCertificateStatus = 'processando' | 'disponivel' | 'erro' | 'sem_coletas';

export interface PlantonCertificate {
  /** Id do gerador como texto — a tela compara com os ids do store, que são texto. */
  generator_id: string;
  ano: number;
  mes: number;
  status: PlantonCertificateStatus | string;
  /** Só em `disponivel`; vale 1 h. */
  signed_url?: string | null;
}

interface RawPlantonCertificate {
  generator_id: number | string;
  ano: number;
  mes: number;
  status: string;
  signed_url?: string | null;
}

const normalizeCertificate = (raw: RawPlantonCertificate): PlantonCertificate => ({
  generator_id: String(raw.generator_id),
  ano: raw.ano,
  mes: raw.mes,
  status: raw.status,
  signed_url: raw.signed_url ?? null,
});

/** Teto de `generatorId` por chamada na rota. */
export const PLANTON_GENERATOR_IDS_LIMIT = 1000;

/**
 * Certificados dos geradores pedidos (`generatorId` repetido, até 1000) ou, sem ids, de todos os
 * geradores do escopo do token — é assim que o gerador externo busca tudo de uma vez. O realm
 * `musa` sem ids responde 400; gerador fora do escopo, 403. Do mais recente ao mais antigo.
 */
export async function fetchPlantonCertificates(generatorIds?: number[] | null, ano?: number): Promise<PlantonCertificate[]> {
  const result = await request<RawPlantonCertificate[] | null>(
    `/ops/generator/planton/certificates${repeatedQueryString({ generatorId: generatorIds?.length ? generatorIds : undefined, ano })}`,
  );
  return (result ?? []).map(normalizeCertificate);
}

/**
 * Um certificado. Ausente → o serviço cria `processando` e emite em background; `erro`/`sem_coletas`
 * → volta a `processando` e emite. O portal faz polling, como sempre fez.
 */
export async function fetchPlantonCertificate(generatorId: number, ano: number, mes: number): Promise<PlantonCertificate> {
  const result = await request<RawPlantonCertificate>(`/ops/generator/planton/certificates/${generatorId}/${ano}/${mes}`);
  return normalizeCertificate(result);
}

/** ZIP com os certificados `disponivel` pedidos (1..500). 400 lista vazia, 403 gerador fora do escopo, 404 nenhum arquivo. */
export async function downloadPlantonCertificatesBulk(
  certificates: Array<{ generator_id: number; ano: number; mes: number }>,
  timeoutMs = 120000,
): Promise<Blob> {
  return requestBlob('/ops/generator/planton/certificates/bulk-download', { certificates }, timeoutMs);
}
